import { useQuery } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import { useAuthApi } from "../api/authApi/useAuthApi"
import { PLATFORM_PERMISSIONS } from "../constants/permissions"
import type { PermissionsResponse, Resource, ResourceAbility } from "../types/PermissionType"
import { hasAnyPermissions } from "../utils/hasAnyPermissions/hasAnyPermissions"
import { useAuth } from "./useAuth"

const QUERY_KEY = "authorization"

/**
 * Checks whether a user is an admin.
 *
 * Precedence rule (intentional for backwards compatibility):
 * - **New FGA**: when `permissions.platform` is defined, it takes precedence (even an empty array means "not admin").
 * - **Old FGA**: only when `permissions.platform` is undefined, fall back to `permissions.meta.isAdmin`.
 */
function isAdminUser(permissions: PermissionsResponse | undefined): boolean {
  if (!permissions) {
    return false
  }
  const { platform, meta } = permissions
  return platform?.includes(PLATFORM_PERMISSIONS.ADMINISTER) ?? meta.isAdmin ?? false
}

/**
 * Checks if user has access to customers.
 * New FGA: admins, users with platform access, or users with any customer-level permissions.
 * Old FGA: check via hasAnyPermissions on "customers" resource.
 * Fallback: use meta.hasPermissions.
 */
function hasCustomerAccess(permissions: PermissionsResponse | undefined): boolean {
  if (!permissions) {
    return false
  }

  // Admins always have access
  if (isAdminUser(permissions)) {
    return true
  }

  // New FGA: check if user has platform access
  if (permissions.platform?.includes(PLATFORM_PERMISSIONS.ACCESS)) {
    return true
  }

  // New FGA: check if user has any resource-level permissions
  if (permissions.resources && Object.keys(permissions.resources).length > 0) {
    return true
  }

  // Old FGA: check old permission format
  const customerPermissions = permissions.data?.find(({ resource }) => resource === "customers")
  if (customerPermissions && hasAnyPermissions(customerPermissions)) {
    return true
  }

  // Final fallback: use meta flag (for transitional period between FGA and old FGA)
  return permissions.meta.hasPermissions ?? false
}

/**
 * Checks if the user has a specific permission (action) on a given resource.
 *
 * @param permissions The loaded permissions for the user
 * @param action The action to check (e.g., "view_deployments", "update")
 * @param resourceId The resource ID (e.g., "customer:woolworths-uuid")
 * @returns true if the permission is granted, false otherwise
 */
function userCan(permissions: PermissionsResponse | undefined, action: ResourceAbility, resourceId: Resource): boolean {
  if (!permissions) {
    return false
  }

  // Admins can do everything
  if (isAdminUser(permissions)) {
    return true
  }

  /**
   * Backwards compatibility (old FGA):
   *
   * The old permissions payload (`permissions.data`) doesn't carry row-level resource IDs like `customer:${id}`.
   * Historically, having any access to the "customers" resource implied access to customer functionality
   * (since the UI didn't perform per-action/per-resource checks).
   *
   * So when the backend does not provide `permissions.resources`, we allow customer actions if the user has
   * *any* old FGA permissions for customers.
   */
  // Important: only fall back to legacy permissions when we're actually on the old schema.
  // In new FGA `resources` can legitimately be null/empty (e.g. viewer-only users), and MUST NOT
  // grant broad access based on legacy `data`.
  if (!permissions.platform && !permissions.resources) {
    const oldFgaCustomerPermissions = permissions.data?.find(({ resource }) => resource === "customers")
    return oldFgaCustomerPermissions ? hasAnyPermissions(oldFgaCustomerPermissions) : false
  }

  const resourcePermissions = permissions.resources?.[resourceId]
  return resourcePermissions?.includes(action) ?? false
}

export const useAuthorization = () => {
  const authApi = useAuthApi()
  const auth = useAuth()

  const query = useQuery({
    queryKey: [QUERY_KEY, auth.type],
    queryFn: authApi.getUserPermissions
  })

  const hasAccessToCustomers = useMemo(() => hasCustomerAccess(query.data), [query.data])

  // Check if the user has a specific permission (action) on a given resource.
  const can = useCallback(
    (action: ResourceAbility, resourceId: Resource): boolean => {
      return userCan(query.data, action, resourceId)
    },
    [query.data]
  )

  return {
    isLoading: query.isLoading,
    isUnauthorized: !query.data?.meta.hasPermissions,
    retry: query.refetch,
    error: query.error,
    isError: query.isError,
    hasAccessToCustomers,
    isAdmin: isAdminUser(query.data),
    can
  }
}
